import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import { crmDashboardApi } from '../../api/crmApi';
import { LeadStatusBadge, SourceBadge } from './components/LeadStatusBadge';
import './CRM.css';

const PALETTE = ['#F97316', '#6366F1', '#0EA5E9', '#10B981', '#94A3B8'];

const CRM_DASHBOARD_RESPONSIVE_STYLES = `
  @keyframes crm-refresh-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .crm-refresh-icon-spinning {
    animation: crm-refresh-spin 1s linear infinite !important;
    transform-origin: 50% 50%;
    transform-box: fill-box;
    will-change: transform;
  }
  /* Dashboard-specific compact responsive layout. These rules intentionally override
     the generic CRM auto-fill grid so two-card sections use the full available width. */
  .crm-dashboard-page {
    gap: 14px;
    min-width: 0;
    width: 100%;
  }
  .crm-dashboard-page .crm-dashboard-grid-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    width: 100%;
    min-width: 0;
    gap: 14px;
    align-items: stretch;
  }
  .crm-dashboard-page .crm-dashboard-grid-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
    width: 100%;
    min-width: 0;
    gap: 14px;
    align-items: stretch;
  }
  .crm-dashboard-page .crm-chart-card {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    padding: 16px;
  }
  .crm-dashboard-page .crm-chart-title {
    margin-bottom: 8px;
  }
  .crm-dashboard-page .crm-chart-card .recharts-responsive-container {
    min-width: 0;
  }
  .crm-dashboard-page .crm-dashboard-tables-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }
  .crm-dashboard-page .crm-table-wrap {
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
  }
  .crm-dashboard-page .crm-table-head {
    min-width: 0;
    padding: 13px 16px;
    gap: 10px;
  }
  .crm-dashboard-page .crm-table-title {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 7px;
    line-height: 1.25;
  }
  .crm-dashboard-page .crm-table-head .crm-btn {
    flex-shrink: 0;
  }
  .crm-dashboard-page .crm-dashboard-table-scroll {
    min-width: 0;
  }
  .crm-dashboard-page .crm-dashboard-table-scroll .crm-table {
    min-width: 0;
    width: 100%;
  }
  .crm-dashboard-page .crm-dashboard-table-scroll .crm-table th,
  .crm-dashboard-page .crm-dashboard-table-scroll .crm-table td {
    padding-top: 10px;
    padding-bottom: 10px;
  }
  .crm-dashboard-page .crm-dashboard-table-scroll .crm-table th:first-child,
  .crm-dashboard-page .crm-dashboard-table-scroll .crm-table td:first-child {
    padding-left: 16px;
  }
  .crm-dashboard-page .crm-dashboard-table-scroll .crm-table th:last-child,
  .crm-dashboard-page .crm-dashboard-table-scroll .crm-table td:last-child {
    padding-right: 16px;
  }
  .crm-dashboard-page > .crm-table-wrap {
    width: 100%;
  }
  @media (max-width: 1100px) {
    .crm-dashboard-page .crm-dashboard-grid-3 {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
  }
  @media (max-width: 760px) {
    .crm-dashboard-page .crm-dashboard-grid-2,
    .crm-dashboard-page .crm-dashboard-grid-3,
    .crm-dashboard-page .crm-dashboard-tables-grid {
      grid-template-columns: 1fr !important;
      gap: 12px;
    }
    .crm-dashboard-page .crm-chart-card {
      padding: 14px;
    }
    .crm-dashboard-page .crm-table-head {
      padding: 12px 14px;
    }
    .crm-dashboard-page .crm-dashboard-table-scroll .crm-table {
      min-width: 640px;
    }
  }

  .crm-dashboard-kpi-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr)) !important;
    gap: 14px;
    width: 100%;
  }
  .crm-dashboard-kpi-grid .crm-kpi-card {
    min-width: 0;
    min-height: 82px;
    padding: 14px 16px;
    box-sizing: border-box;
  }
  .crm-dashboard-kpi-grid .crm-kpi-label {
    line-height: 1.35;
    white-space: normal;
    overflow-wrap: anywhere;
  }
  @media (max-width: 1200px) {
    .crm-dashboard-kpi-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
  }
  @media (max-width: 900px) {
    .crm-dashboard-kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
  }
  @media (max-width: 560px) {
    .crm-dashboard-kpi-grid {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      gap: 10px;
    }
    .crm-dashboard-kpi-grid .crm-kpi-card {
      min-height: 0;
      padding: 10px 12px;
      gap: 10px;
      border-radius: 12px;
    }
    .crm-dashboard-kpi-grid .crm-kpi-icon {
      width: 34px;
      height: 34px;
      border-radius: 9px;
    }
    .crm-dashboard-kpi-grid .crm-kpi-value {
      font-size: 1.15rem;
    }
    .crm-dashboard-kpi-grid .crm-kpi-label {
      font-size: 0.68rem;
      margin-top: 1px;
    }
  }
  @media (max-width: 380px) {
    .crm-dashboard-kpi-grid { gap: 8px; }
    .crm-dashboard-kpi-grid .crm-kpi-card { padding: 9px 10px; gap: 8px; }
    .crm-dashboard-kpi-grid .crm-kpi-icon { width: 30px; height: 30px; }
    .crm-dashboard-kpi-grid .crm-kpi-value { font-size: 1.05rem; }
    .crm-dashboard-kpi-grid .crm-kpi-label { font-size: 0.64rem; }
  }

  /* Buttons scale down with the screen so nothing overflows on phones. */
  @media (max-width: 900px) {
    .crm-dashboard-page .crm-btn {
      height: 38px;
      min-height: 0;
      padding: 0 14px;
      font-size: 0.8rem;
      gap: 6px;
    }
    /* global-theme.css forces [class*="actions"] to a column on mobile,
       which also matches .crm-header-actions — keep these side by side. */
    .crm-dashboard-page .crm-header-actions {
      flex-direction: row;
      align-items: center;
      flex-wrap: nowrap;
      align-self: flex-end;
      width: auto;
      gap: 8px;
    }
    .crm-dashboard-page .crm-header-actions .crm-btn {
      flex: 0 0 auto;
      width: auto;
    }
    .crm-dashboard-page .crm-card-view-btn {
      height: 32px;
      min-width: 64px;
      padding: 0 11px;
      font-size: 11px;
    }
    .crm-dashboard-page .crm-card-view-btn span { font-size: 13px; }
    .crm-dashboard-page .crm-source-link,
    .crm-dashboard-page .crm-funnel-link {
      font-size: 0.7rem;
    }
  }
  @media (max-width: 560px) {
    .crm-dashboard-page .crm-btn {
      height: 34px;
      min-height: 0;
      padding: 0 12px;
      font-size: 0.76rem;
      gap: 5px;
      border-radius: 9px;
      white-space: nowrap;
    }
    .crm-dashboard-page .crm-header-actions {
      width: auto;
      gap: 8px;
      flex-wrap: nowrap;
    }
    .crm-dashboard-page .crm-header-actions .crm-btn {
      flex: 0 0 auto;
      width: auto;
      height: 32px;
      min-height: 0;
      padding: 0 10px;
      font-size: 0.72rem;
      gap: 5px;
      border-radius: 9px;
      justify-content: center;
      white-space: nowrap;
    }
    .crm-dashboard-page .crm-btn svg {
      width: 14px;
      height: 14px;
    }
    .crm-dashboard-page .crm-card-view-btn {
      height: 28px;
      min-width: 56px;
      padding: 0 9px;
      font-size: 10.5px;
      gap: 4px;
    }
    .crm-dashboard-page .crm-card-view-btn span { font-size: 12px; }
    .crm-dashboard-page .crm-source-link,
    .crm-dashboard-page .crm-funnel-link {
      font-size: 0.68rem;
      padding: 5px 0;
    }
  }

  /* Responsive dashboard tables */
  .crm-dashboard-table-card {
    min-width: 0;
    width: 100%;
    overflow: hidden;
  }
  .crm-dashboard-table-scroll {
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    overscroll-behavior-x: contain;
  }
  .crm-dashboard-table-scroll::-webkit-scrollbar {
    height: 7px;
  }
  .crm-dashboard-table-scroll::-webkit-scrollbar-thumb {
    background: var(--border, #d9deea);
    border-radius: 10px;
  }
  .crm-dashboard-table-scroll::-webkit-scrollbar-track {
    background: transparent;
  }
  .crm-dashboard-table-scroll .crm-table {
    width: 100%;
    min-width: 560px;
    table-layout: auto;
    border-collapse: separate;
    border-spacing: 0;
  }
  .crm-dashboard-table-scroll .crm-table th,
  .crm-dashboard-table-scroll .crm-table td {
    white-space: nowrap;
    vertical-align: middle;
  }
  .crm-dashboard-table-scroll .crm-table th:first-child,
  .crm-dashboard-table-scroll .crm-table td:first-child {
    padding-left: 18px;
  }
  .crm-dashboard-table-scroll .crm-table th:last-child,
  .crm-dashboard-table-scroll .crm-table td:last-child {
    padding-right: 18px;
  }
  .crm-dashboard-table-scroll .crm-table td:first-child,
  .crm-dashboard-table-scroll .crm-table td:nth-child(2) {
    white-space: normal;
    min-width: 130px;
  }
  .crm-dashboard-table-scroll .crm-table thead th {
    position: sticky;
    top: 0;
    z-index: 1;
  }
  .crm-table-head {
    min-width: 0;
    gap: 12px;
    flex-wrap: wrap;
  }
  .crm-table-title {
    min-width: 0;
    flex: 1 1 220px;
  }
  .crm-table-head .crm-btn {
    flex: 0 0 auto;
  }
  @media (max-width: 900px) {
    .crm-dashboard-table-scroll .crm-table {
      min-width: 620px;
    }
    .crm-dashboard-table-card {
      width: 100%;
    }
  }
  @media (max-width: 600px) {
    .crm-dashboard-table-scroll .crm-table {
      min-width: 640px;
      font-size: 0.78rem;
    }
    .crm-dashboard-table-scroll .crm-table th,
    .crm-dashboard-table-scroll .crm-table td {
      padding: 10px 12px;
    }
    .crm-table-head {
      align-items: center;
    }
    .crm-table-title {
      flex-basis: 180px;
      font-size: 0.92rem;
    }
  }
`;

const Icon = ({ name, size = 18, strokeWidth = 1.9, className = '' }) => {
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    className,
    'aria-hidden': 'true',
    focusable: 'false',
  };

  const paths = {
    dashboard: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
    leads: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    factory: <><path d="M3 21V9l6 3V9l6 3V5h6v16" /><path d="M3 21h18" /><path d="M17 9h2M17 13h2M17 17h2" /></>,
    user: <><circle cx="12" cy="7" r="4" /><path d="M5.5 21a6.5 6.5 0 0 1 13 0" /></>,
    phone: <><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.2 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.9.33 1.78.62 2.63a2 2 0 0 1-.45 2.11L8 9.73a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.85.29 1.73.5 2.63.62A2 2 0 0 1 22 16.92z" /></>,
    calendar: <><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /><path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" /></>,
    alert: <><path d="M10.3 3.3 2.4 17a2 2 0 0 0 1.73 3h15.74a2 2 0 0 0 1.73-3L13.7 3.3a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>,
    check: <><circle cx="12" cy="12" r="9" /><path d="m8 12 2.5 2.5L16 9" /></>,
    handshake: <><path d="m11 7 2-2 3 3 2-2 4 4-3 3" /><path d="m3 11 4-4 4 4-4 4-4-4Z" /><path d="m7 15 3 3 2-2 2 2 4-4" /><path d="M13 5 9 9" /></>,
    chart: <><path d="M4 19V5M4 19h17" /><path d="m7 15 4-5 3 3 5-7" /></>,
    refresh: <><path d="M20 11a8 8 0 0 0-14.5-4.5L4 8" /><path d="M4 4v4h4" /><path d="M4 13a8 8 0 0 0 14.5 4.5L20 16" /><path d="M20 20v-4h-4" /></>,
    plus: <><path d="M12 5v14M5 12h14" /></>,
    building: <><path d="M3 21h18" /><path d="M5 21V5l7-3v19" /><path d="M19 21V9l-7-3" /><path d="M8 7h1M8 11h1M8 15h1M15 11h1M15 15h1" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    target: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" /></>,
  };

  return <svg {...common}>{paths[name] || paths.dashboard}</svg>;
};


const emptyStats = {
  newLeads: 0,
  indiaMartLeads: 0,
  myLeads: 0,
  todaysCalls: 0,
  todaysFollowups: 0,
  overdueFollowups: 0,
  qualifiedLeads: 0,
  convertedCustomers: 0,
};

const kpiConfig = [
  { key: 'newLeads', label: 'New Leads', color: '#6366F1', route: '/crm/my-leads', icon: 'leads' },
  { key: 'indiaMartLeads', label: 'IndiaMART Leads', color: '#F97316', route: '/crm/india-mart-leads', icon: 'factory' },
  { key: 'myLeads', label: 'My Leads', color: '#0EA5E9', route: '/crm/my-leads', icon: 'user' },
  { key: 'todaysCalls', label: "Today's Calls", color: '#10B981', route: '/crm/calls', icon: 'phone' },
  { key: 'todaysFollowups', label: "Today's Follow-ups", color: '#F59E0B', route: '/crm/followups', icon: 'calendar' },
  { key: 'overdueFollowups', label: 'Overdue Follow-ups', color: '#EF4444', route: '/crm/followups', icon: 'alert' },
  { key: 'qualifiedLeads', label: 'Qualified Leads', color: '#8B5CF6', route: '/crm/my-leads', icon: 'check' },
  { key: 'convertedCustomers', label: 'Converted Customers', color: '#0D9488', route: '/dashboard/customers/view', icon: 'handshake' },
];

const safeNum = (v) => Number(v || 0);
const money = (v) => `₹${safeNum(v).toLocaleString('en-IN')}`;

const FUNNEL_STAGES = [
  { key: 'New', label: 'New', color: '#6366F1' },
  { key: 'Contacted', label: 'Contacted', color: '#3B82F6' },
  { key: 'Interested', label: 'Interested', color: '#0EA5E9' },
  { key: 'Qualified', label: 'Qualified', color: '#8B5CF6' },
  { key: 'Requirement', label: 'Requirement', color: '#A855F7' },
  { key: 'Quotation', label: 'Quotation', color: '#14B8A6' },
  { key: 'Negotiation', label: 'Negotiation', color: '#F59E0B' },
  { key: 'Won', label: 'Won', color: '#10B981' },
];

const FUNNEL_ALIASES = {
  new: ['new', 'new lead', 'new leads'],
  contacted: ['contacted', 'contact'],
  interested: ['interested'],
  qualified: ['qualified', 'qualified lead', 'qualified leads'],
  requirement: ['requirement', 'requirements'],
  quotation: ['quotation', 'quote', 'quoted'],
  negotiation: ['negotiation', 'negotiating'],
  won: ['won', 'closed won', 'converted', 'converted customer', 'converted customers'],
};

const getFunnelValue = (item) => safeNum(
  item?.value ?? item?.count ?? item?.total ?? item?.leads ?? item?.opportunities
);

const normalizeFunnelStage = (value = '') => String(value).trim().toLowerCase().replace(/[_-]+/g, ' ');

const buildFunnelStages = (rows = []) => FUNNEL_STAGES.map((stage) => {
  const aliases = FUNNEL_ALIASES[stage.key.toLowerCase()] || [normalizeFunnelStage(stage.key)];
  const match = rows.find((row) => {
    const name = normalizeFunnelStage(row?.name ?? row?.stage ?? row?.status ?? row?.label);
    return aliases.includes(name);
  });
  return { ...stage, value: getFunnelValue(match) };
});


const SkeletonBlock = ({ width = '100%', height = 16, radius = 8, style = {} }) => (
  <span className="crm-skeleton" style={{ width, height, borderRadius: radius, ...style }} />
);

const ChartSkeletonCard = ({ height = 205 }) => (
  <div className="crm-chart-card">
    <SkeletonBlock width={170} height={14} radius={7} style={{ marginBottom: 18 }} />
    <SkeletonBlock width="100%" height={height} radius={10} />
  </div>
);

const TableSkeletonCard = ({ rows = 4, cols = 4 }) => (
  <div className="crm-table-wrap crm-dashboard-table-card">
    <div className="crm-table-head">
      <SkeletonBlock width={170} height={16} />
      <SkeletonBlock width={76} height={30} radius={9} />
    </div>
    <div style={{ padding: '16px 20px' }}>
      {[...Array(rows)].map((_, r) => (
        <div key={r} style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
          {[...Array(cols)].map((_, c) => (
            <SkeletonBlock key={c} height={14} radius={7} style={{ flex: c === 0 ? 1.4 : 1 }} />
          ))}
        </div>
      ))}
    </div>
  </div>
);


function LeadSourceCard({ data = [], navigate }) {
  const rows = (Array.isArray(data) ? data : [])
    .map((item, index) => ({
      name: item?.name ?? item?.source ?? item?.label ?? `Source ${index + 1}`,
      value: getFunnelValue(item),
      color: PALETTE[index % PALETTE.length],
    }))
    .filter((item) => item.value >= 0);

  const total = rows.reduce((sum, item) => sum + item.value, 0);
  const visibleRows = rows.filter((item) => item.value > 0).sort((a, b) => b.value - a.value);
  const topRows = visibleRows.slice(0, 5);
  const otherValue = visibleRows.slice(5).reduce((sum, item) => sum + item.value, 0);
  if (otherValue > 0) topRows.push({ name: 'Other', value: otherValue, color: '#94A3B8' });

  return (
    <div className="crm-chart-card crm-source-card">
      <div className="crm-source-header">
        <div className="crm-source-title-row">
          <span className="crm-source-title-icon"><Icon name="chart" size={16} /></span>
          <div>
            <h3 className="crm-chart-title">Lead Source Distribution</h3>
            <p className="crm-source-subtitle">Where your leads are coming from</p>
          </div>
        </div>
        <button className="crm-source-link" onClick={() => navigate('/crm/leads')} type="button">
          View leads <span aria-hidden="true">→</span>
        </button>
      </div>

      {total === 0 ? (
        <div className="crm-source-empty">
          <div className="crm-source-empty-icon"><Icon name="chart" size={20} /></div>
          <strong>No lead sources yet</strong>
          <span>Add or import leads to see source distribution.</span>
        </div>
      ) : (
        <div className="crm-source-body">
          <div className="crm-source-donut" aria-label={`Total ${total} leads`}>
            <div className="crm-source-donut-ring" style={{ background: `conic-gradient(${topRows.map((item, i) => {
              const start = topRows.slice(0, i).reduce((sum, row) => sum + (row.value / total) * 360, 0);
              const end = start + (item.value / total) * 360;
              return `${item.color} ${start}deg ${end}deg`;
            }).join(', ')})` }}>
              <div className="crm-source-donut-center">
                <strong>{total.toLocaleString('en-IN')}</strong>
                <span>Total leads</span>
              </div>
            </div>
          </div>

          <div className="crm-source-list">
            {topRows.map((item) => {
              const percent = Math.round((item.value / total) * 100);
              return (
                <button
                  className="crm-source-row"
                  type="button"
                  key={item.name}
                  onClick={() => navigate(`/crm/leads?source=${encodeURIComponent(item.name)}`)}
                >
                  <span className="crm-source-row-head">
                    <span className="crm-source-name"><i style={{ background: item.color }} />{item.name}</span>
                    <strong>{item.value.toLocaleString('en-IN')} <em>{percent}%</em></strong>
                  </span>
                  <span className="crm-source-track">
                    <span style={{ width: `${percent}%`, background: item.color }} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function SalesFunnelCard({ data, navigate }) {
  const stages = buildFunnelStages(Array.isArray(data) ? data : []);
  const maxValue = Math.max(...stages.map((stage) => stage.value), 0);
  const totalLeads = maxValue;
  const wonCount = stages.find((stage) => stage.key === 'Won')?.value || 0;
  const lostItem = (Array.isArray(data) ? data : []).find((row) =>
    normalizeFunnelStage(row?.name ?? row?.stage ?? row?.status ?? row?.label) === 'lost'
  );
  const lostCount = getFunnelValue(lostItem);
  const winRate = totalLeads > 0 ? Math.round((wonCount / totalLeads) * 100) : 0;

  return (
    <div className="crm-chart-card crm-sales-funnel-card">
      <div className="crm-funnel-header">
        <div className="crm-funnel-title-row">
          <span className="crm-funnel-title-icon"><Icon name="target" size={16} /></span>
          <div>
            <h3 className="crm-chart-title">Sales Funnel</h3>
            <p className="crm-funnel-subtitle">Track leads from first contact to closed deal</p>
          </div>
        </div>
        <button className="crm-funnel-link" onClick={() => navigate('/crm/pipeline')} type="button">
          View pipeline <span aria-hidden="true">→</span>
        </button>
      </div>

      <div className="crm-funnel-summary">
        <div className="crm-funnel-summary-item">
          <span className="crm-funnel-summary-label">Total leads</span>
          <strong>{totalLeads.toLocaleString('en-IN')}</strong>
        </div>
        <div className="crm-funnel-summary-divider" />
        <div className="crm-funnel-summary-item">
          <span className="crm-funnel-summary-label">Won</span>
          <strong className="is-success">{wonCount.toLocaleString('en-IN')}</strong>
        </div>
        <div className="crm-funnel-summary-divider" />
        <div className="crm-funnel-summary-item">
          <span className="crm-funnel-summary-label">Win rate</span>
          <strong>{winRate}%</strong>
        </div>
      </div>

      {maxValue === 0 ? (
        <div className="crm-funnel-empty">
          <div className="crm-funnel-empty-icon"><Icon name="chart" size={20} /></div>
          <strong>No pipeline data yet</strong>
          <span>Create or import a lead to start tracking your sales funnel.</span>
          <button className="crm-btn crm-btn-primary crm-btn-sm" onClick={() => navigate('/crm/leads/new')} type="button">
            <Icon name="plus" size={14} /> Add Lead
          </button>
        </div>
      ) : (
        <>
          <div className="crm-funnel-visual" role="list" aria-label="Sales funnel stages">
            {stages.map((stage, index) => {
              const previous = index > 0 ? stages[index - 1].value : totalLeads;
              const conversion = previous > 0 ? Math.round((stage.value / previous) * 100) : null;
              const width = Math.max(34, (stage.value / maxValue) * 100);

              return (
                <button
                  className="crm-funnel-row"
                  key={stage.key}
                  type="button"
                  role="listitem"
                  aria-label={`View ${stage.label} leads, ${stage.value}`}
                  onClick={() => navigate(`/crm/leads?status=${encodeURIComponent(stage.key)}`)}
                >
                  <span className="crm-funnel-row-label">
                    <span className="crm-funnel-stage-index">{index + 1}</span>
                    <span className="crm-funnel-stage-name">{stage.label}</span>
                  </span>
                  <span className="crm-funnel-shape-wrap">
                    <span
                      className="crm-funnel-shape"
                      style={{
                        width: `${width}%`,
                        background: `linear-gradient(90deg, ${stage.color}, ${stage.color}DD)`,
                      }}
                    >
                      <span className="crm-funnel-count">{stage.value.toLocaleString('en-IN')}</span>
                    </span>
                  </span>
                  <span className="crm-funnel-conversion">
                    {index === 0 ? '100%' : conversion === null ? '—' : `${conversion}%`}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="crm-funnel-outcomes">
            <div className="crm-funnel-outcome is-won">
              <span className="crm-funnel-outcome-dot" />
              <span>Won</span>
              <strong>{wonCount.toLocaleString('en-IN')}</strong>
            </div>
            <div className="crm-funnel-outcome is-lost">
              <span className="crm-funnel-outcome-dot" />
              <span>Lost</span>
              <strong>{lostCount.toLocaleString('en-IN')}</strong>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


export default function CRMDashboard({ embedded = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [stats, setStats] = useState(emptyStats);
  const [sourceData, setSourceData] = useState([]);
  const [funnelData, setFunnelData] = useState([]);
  const [trendData, setTrendData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [conversionData, setConversionData] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [imLeads, setImLeads] = useState([]);
  const [recentCalls, setRecentCalls] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const calls = await Promise.allSettled([
      crmDashboardApi.getStats(),
      crmDashboardApi.getLeadSourceDistribution(),
      crmDashboardApi.getSalesFunnel(),
      crmDashboardApi.getMonthlyLeadTrend(),
      crmDashboardApi.getSalespersonActivity(),
      crmDashboardApi.getQuotationConversion(),
      crmDashboardApi.getLeadConversion(),
      crmDashboardApi.getTodaysFollowups(),
      crmDashboardApi.getRecentIndiaMartLeads({ size: 5 }),
      crmDashboardApi.getRecentCalls({ size: 5 }),
      crmDashboardApi.getRecentActivities({ size: 6 }),
    ]);
    const [st, src, fn, tr, act, qc, , fu, im, ca, ac] = calls;
    if (st.status === 'fulfilled' && st.value?.data) setStats({ ...emptyStats, ...st.value.data });
    if (src.status === 'fulfilled' && src.value?.data) setSourceData(src.value.data);
    if (fn.status === 'fulfilled' && fn.value?.data) setFunnelData(fn.value.data);
    if (tr.status === 'fulfilled' && tr.value?.data) setTrendData(tr.value.data);
    if (act.status === 'fulfilled' && act.value?.data) setActivityData(act.value.data);
    if (qc.status === 'fulfilled' && qc.value?.data) setConversionData(qc.value.data);
    if (fu.status === 'fulfilled' && fu.value?.data) setFollowups(fu.value.data);
    if (im.status === 'fulfilled' && im.value?.data) setImLeads(im.value.data);
    if (ca.status === 'fulfilled' && ca.value?.data) setRecentCalls(ca.value.data);
    if (ac.status === 'fulfilled' && ac.value?.data) setRecentActivities(ac.value.data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await fetchAll();
    } finally {
      setIsRefreshing(false);
    }
  }, [fetchAll, isRefreshing]);

  return (
    <div className={`crm-page crm-dashboard-page${embedded ? ' crm-dashboard-embedded' : ''}`}>
      <style>{CRM_DASHBOARD_RESPONSIVE_STYLES}</style>
      {!embedded && (
        <div className="crm-header">
          <div className="crm-header-left">
            <div className="crm-page-icon"><Icon name="chart" size={22} strokeWidth={2} /></div>
            <div>
              <h1 className="crm-title">CRM Dashboard</h1>
              <p className="crm-subtitle">Sales pipeline overview — leads, opportunities & conversions</p>
            </div>
          </div>
          <div className="crm-header-actions">
            <button className="crm-btn crm-btn-ghost" onClick={handleRefresh} disabled={isRefreshing} aria-label="Refresh CRM dashboard"><Icon name="refresh" size={15} className={isRefreshing ? "crm-refresh-icon-spinning" : ""} /> {isRefreshing ? "Refreshing..." : "Refresh"}</button>
            <button className="crm-btn crm-btn-primary" onClick={() => navigate('/crm/leads/new')}><Icon name="plus" size={16} /> New Lead</button>
          </div>
        </div>
      )}

      {/* KPI CARDS */}
      <div className="crm-kpi-grid crm-dashboard-kpi-grid">
        {loading ? (
          kpiConfig.map((k) => (
            <div className="crm-kpi-card" key={k.key}>
              <SkeletonBlock width={44} height={44} radius={11} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <SkeletonBlock width="55%" height={20} style={{ marginBottom: 8 }} />
                <SkeletonBlock width="78%" height={12} radius={6} />
              </div>
            </div>
          ))
        ) : (
          kpiConfig.map((k) => (
            <div className="crm-kpi-card" key={k.key} onClick={() => navigate(k.route)}>
              <div className="crm-kpi-icon" style={{ background: `${k.color}18`, color: k.color }}>
                <Icon name={k.icon} size={20} strokeWidth={2} />
              </div>
              <div>
                <div className="crm-kpi-value">
                  {k.isRupee ? money(stats[k.key]) : stats[k.key] ?? 0}
                </div>
                <div className="crm-kpi-label">{k.label}</div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* CHARTS ROW 1 */}
      <div className="crm-charts-grid crm-dashboard-grid-2">
        {loading ? (
          <>
            <ChartSkeletonCard height={230} />
            <ChartSkeletonCard height={230} />
          </>
        ) : (
          <>
            <LeadSourceCard data={sourceData} navigate={navigate} />
            <SalesFunnelCard data={funnelData} navigate={navigate} />
          </>
        )}
      </div>

      {/* CHARTS ROW 2 */}
      <div className="crm-charts-grid crm-dashboard-grid-3">
        {loading ? (
          <>
            <ChartSkeletonCard />
            <ChartSkeletonCard />
            <ChartSkeletonCard />
          </>
        ) : (
          <>
            <div className="crm-chart-card">
              <h3 className="crm-chart-title">Monthly Lead Trend</h3>
              <ResponsiveContainer width="100%" height={205}>
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12, fill: 'var(--ink-soft)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--ink-soft)' }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="leads" stroke="#6366F1" strokeWidth={2.5} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="converted" stroke="#10B981" strokeWidth={2.5} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="crm-chart-card">
              <h3 className="crm-chart-title">Salesperson Activity</h3>
              <ResponsiveContainer width="100%" height={205}>
                <BarChart data={activityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--ink-soft)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--ink-soft)' }} />
                  <Tooltip />
                  <Bar dataKey="calls" fill="#6366F1" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="followups" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="crm-chart-card">
              <h3 className="crm-chart-title">Quotation Conversion</h3>
              <ResponsiveContainer width="100%" height={205}>
                <BarChart data={conversionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--ink-soft)' }} />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--ink-soft)' }} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#0EA5E9" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      {/* TABLES */}
      <div className="crm-charts-grid crm-dashboard-grid-2 crm-dashboard-tables-grid">
        {loading ? (
          <>
            <TableSkeletonCard rows={4} cols={4} />
            <TableSkeletonCard rows={4} cols={3} />
          </>
        ) : (
          <>
            {/* Today's Follow-ups */}
            <div className="crm-table-wrap crm-dashboard-table-card">
              <div className="crm-table-head">
                <h3 className="crm-table-title"><Icon name="calendar" size={17} /> Today's Follow-ups</h3>
                <button className="crm-card-view-btn" onClick={() => navigate('/crm/followups')}>View All <span aria-hidden="true">→</span></button>
              </div>
              <div className="crm-dashboard-table-scroll"><table className="crm-table">
                <thead><tr><th>Lead</th><th>Type</th><th>Time</th><th>Priority</th></tr></thead>
                <tbody>
                  {followups.map((f) => (
                    <tr key={f.id}>
                      <td><strong>{f.lead}</strong><br /><span style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>{f.company}</span></td>
                      <td>{f.type}</td>
                      <td>{f.time}</td>
                      <td><LeadStatusBadge status={f.priority} /></td>
                    </tr>
                  ))}
                  {!followups.length && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>No follow-ups today</td></tr>}
                </tbody>
              </table></div>
            </div>

            {/* Recent IndiaMART Leads */}
            <div className="crm-table-wrap crm-dashboard-table-card">
              <div className="crm-table-head">
                <h3 className="crm-table-title"><Icon name="building" size={17} /> Recent IndiaMART Leads</h3>
                <button className="crm-card-view-btn" onClick={() => navigate('/crm/india-mart-leads')}>View All <span aria-hidden="true">→</span></button>
              </div>
              <div className="crm-dashboard-table-scroll"><table className="crm-table">
                <thead><tr><th>Lead ID</th><th>Customer</th><th>Requirement</th></tr></thead>
                <tbody>
                  {imLeads.map((l) => (
                    <tr key={l.id}>
                      <td><SourceBadge source="IndiaMART" /></td>
                      <td><strong>{l.customerName}</strong><br /><span style={{ fontSize: '0.72rem', color: 'var(--ink-soft)' }}>{l.company} · {l.city}</span></td>
                      <td style={{ fontSize: '0.79rem' }}>{l.requirement}</td>
                    </tr>
                  ))}
                  {!imLeads.length && <tr><td colSpan={3} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>No IndiaMART leads</td></tr>}
                </tbody>
              </table></div>
            </div>
          </>
        )}
      </div>

      <div className="crm-charts-grid crm-dashboard-grid-2 crm-dashboard-tables-grid">
        {loading ? (
          <>
            <TableSkeletonCard rows={4} cols={4} />
            <TableSkeletonCard rows={3} cols={2} />
          </>
        ) : (
          <>
            {/* Recent Calls */}
            <div className="crm-table-wrap crm-dashboard-table-card">
              <div className="crm-table-head">
                <h3 className="crm-table-title"><Icon name="phone" size={17} /> Recent Calls</h3>
                <button className="crm-card-view-btn" onClick={() => navigate('/crm/calls')}>View All <span aria-hidden="true">→</span></button>
              </div>
              <div className="crm-dashboard-table-scroll"><table className="crm-table">
                <thead><tr><th>Lead</th><th>Outcome</th><th>Duration</th><th>Time</th></tr></thead>
                <tbody>
                  {recentCalls.map((c) => (
                    <tr key={c.id}>
                      <td><strong>{c.lead}</strong></td>
                      <td><LeadStatusBadge status={c.outcome === 'Interested' ? 'Interested' : c.outcome === 'Not Interested' ? 'Lost' : 'Contacted'} /></td>
                      <td>{c.duration}</td>
                      <td>{c.time}</td>
                    </tr>
                  ))}
                  {!recentCalls.length && <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>No recent calls</td></tr>}
                </tbody>
              </table></div>
            </div>

            {/* Recent Activities */}
            <div className="crm-table-wrap crm-dashboard-table-card">
              <div className="crm-table-head">
                <h3 className="crm-table-title"><Icon name="clock" size={17} /> Recent Activities</h3>
                <button className="crm-card-view-btn" onClick={() => navigate('/crm/activities')}>View All <span aria-hidden="true">→</span></button>
              </div>
              <div style={{ padding: '6px 20px 16px' }}>
                <div className="crm-timeline">
                  {recentActivities.map((a, i) => (
                    <div className="crm-timeline-item" key={a.id || i}>
                      <div className="crm-timeline-dot" style={{ background: '#6366F1' }} />
                      <div className="crm-timeline-date">{a.time}</div>
                      <div className="crm-timeline-title">{a.title}</div>
                      {a.desc && <div className="crm-timeline-desc">{a.desc}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

    </div>
  );
}
